import { action, observable, computed } from 'mobx';

import { AppManager, Input, routerStore, Variables } from 'core';
import { JobSpecAppManager } from 'apps/jobSpec/appManager';
import Alert from 'react-s-alert';
import { FormStore } from 'libs/forms/app';

import { whenever } from 'core/whenever';

import { RFQProductQuotesQ } from 'apps/rfq/gql';
import 'apps/manufacturer/gql'; // this adds its queries to the registry, which we need for forms

import { formatISO } from 'date-fns';

import {
  ProductQuoteApp,
  ProductQuoteModel,
  ProductQuotesArrayApp,
} from './app';
import {
  CreateProductQuote,
  DeleteProductQuoteMutation,
  ExtendProductQuote,
} from './gql';
import { productQuoteFromRFQ } from './utils';
import {
  productQuoteFormConfig,
  extendQuoteFormConfig,
} from './ProductQuoteFormConfg';

export class ProductQuoteAppManager extends AppManager {
  @observable productQuoteForm;
  @observable savedProductQuote;
  @observable productQuoteApp;
  @observable productQuotes;
  @observable quoteAdded = false;
  @observable quoteDeleted = false;
  @observable extendProductQuoteId;
  @observable extendQuoteDialogVisible = false;

  constructor(
    parent,
    { productQuoteApp, fromRFQ, shipmentQuoteAppManager },
    options,
  ) {
    super(parent, {}, options);

    if (shipmentQuoteAppManager) {
      this.shipmentQuoteAppManager = shipmentQuoteAppManager;
      this.shipmentQuoteAppManager.currentProductQuoteAppManager = this;
    }

    this.productQuoteApp = productQuoteApp;

    if (!this.productQuoteApp && fromRFQ) {
      this.productQuoteApp = productQuoteFromRFQ(
        this,
        fromRFQ,
        this.shipmentQuoteAppManager,
      );
    }

    this.jobSpecManager = new JobSpecAppManager(this, {
      jobSpecApp: this.productQuoteApp.for,
      isQuote: true,
    });
    this.AppRef('savedProductQuote', ProductQuoteApp);
    this.AppRef('productQuotes', ProductQuotesArrayApp);
    this.setUpCreateProductQuote();

    this.initialiseQuoteForm();

    this.initialiseProductQuotesList();
    this.disposers.push(
      whenever(() => this.productQuoteApp?.submitting, this.AddProductQuote),
      whenever(
        () => this.quoteAdded,
        () => {
          if (this.productQuoteApp.toAdd) {
            // if the user clicked 'Add To Shipment'
            this.shipmentQuoteAppManager?.AddProductQuoteId(
              this.savedProductQuote,
            );
          }
          this.productQuotesQ.refetchQuery();
          this.SetQuoteAdded(false);
          this.initialiseQuoteForm();
        },
      ),
      whenever(
        () => this.quoteDeleted,
        () => {
          // Should deal with race condition here by cancelling/ignoring results of previous refeteches
          // Currently it's possible if deleting multiple quotes to get these refetches out of order
          this.productQuotesQ.refetchQuery();
          this.SetQuoteDeleted(false);
        },
      ),
      whenever(
        () => this.deleteInitiatedOn,
        () => {
          const pq = this.deleteInitiatedOn;
          pq.SetDeleteInitiated(false);
          pq.SetDeleting(true);
          if (routerStore.params.get('pqids').includes(pq.dgraphId)) {
            this.shipmentQuoteAppManager?.RemoveProductQuoteId(pq);
          }
          this.DeleteProductQuote(pq);
        },
      ),
    );
  }

  @computed get deleteInitiatedOn() {
    // can only be true for one product quote at a time as it is triggered synchronously
    return this.productQuotes.find(pq => pq.deleteInitiated);
  }

  @action SetQuoteAdded = boolVal => {
    this.quoteAdded = boolVal;
  };

  @action SetQuoteDeleted = boolVal => {
    this.quoteDeleted = boolVal;
  };

  @action UpdateProductQuotesList = productQuotes => {
    this.productQuotes = productQuotes;
  };

  @action initialiseProductQuotesList = () => {
    this.variables = new Variables({
      id: () => this.productQuoteApp.from.id,
    });
    this.productQuotesQ = new RFQProductQuotesQ(this, {
      queryKey: 'rfqProductQuotes',
      // variables: this.variables,
    });
    this.productQuotesQ.process(productQuotes => {
      if (productQuotes === null) {
        return [];
      }
      return productQuotes.map(productQ => ProductQuoteModel.Load(productQ));
    }, this.UpdateProductQuotesList);

    this.productQuotesQ.query();
    this.variables.onChange(
      ['id'],
      () => {
        this.productQuotesQ.refetchQuery();
      },
      false,
    );
  };

  @action setUpCreateProductQuote = () => {
    this.productQuoteInput = new Input({
      input: () => {
        const { currency, cost, quantity, supplier, from } =
          this.productQuoteApp;
        const { leadTime, build } = this.productQuoteApp.for;

        const optionValues = this.productQuoteApp.for.build.valuesTarget;

        const buildValues = Object.values(optionValues)
          .filter(
            optVal => !(optVal === null || optVal.option.name === 'Lead Time'),
          )
          .map(optVal => {
            const buildVal = {
              optionId: optVal.option.id,
            };
            buildVal[optVal.option.valueKey] = optVal.value;
            return buildVal;
          });

        return {
          cost,
          currency,
          fromId: from?.id,
          isEstimated: false,
          newJobSpec: {
            quantity,
            leadTime,
            build: {
              // fileId: 1,
              materialId: build.material.id,
              processId: build.process.id,
              buildValues,
              /* supportingFiles: {
                toAdd: [],
                toDelete: [],
              }, */
            },
          },
          supplierId: supplier?.id,
          // validUntil: 'x-x-x',
        };
      },
    });

    this.createProductQuote = new CreateProductQuote(
      this.productQuoteInput,
      null,
      {
        onSuccess: this.onSuccess,
        onSysErr: this.onSysErr,
        onUserErr: this.onSysErr,
      },
      // {},
      true,
    );

    this.createProductQuote.process(
      quote => ProductQuoteModel.Load(quote),
      this.CompleteAddQuote,
    );
  };

  onSysErr = (/* error */) => {
    Alert.error('Could not create the Quote');
  };

  onSuccess = () => {
    Alert.success('Product Quote created successfully.');
    this.productQuoteApp.unitCost = '';
  };

  @action initialiseQuoteForm = () => {
    this.productQuoteFormStore = new FormStore(
      this.productQuoteApp,
      productQuoteFormConfig,
      'productQuoteForm',
      false, // autoFocusFirstField
    );

    this.extendQuoteFormStore = new FormStore(
      this.productQuoteApp,
      extendQuoteFormConfig,
      'extendQuoteForm',
      false, // autoFocusFirstField
    );

    const currentDate = new Date();

    currentDate.setDate(currentDate.getDate() + 7);

    this.extendQuoteFormStore.fieldStores
      .get('validUntil')
      .UpdateValue(currentDate);
  };

  @computed get isValid() {
    return this.productQuoteFormStore.isValid && this.jobSpecManager.isValid;
  }

  @action SetExtendQuoteDialogVisible = visible => {
    this.extendQuoteDialogVisible = visible;
  };

  @action SetExtendQuoteId = id => {
    this.extendProductQuoteId = id;
  };

  @action ExtendProductQuoteErr = err => {
    this.extendQuoteFormStore.SetPosting(false);
    if (err?.message) {
      Alert.error(err.message);
    }
  };

  @action ExtendProductQuoteSuccess = () => {
    Alert.success('Quote extended successfully.');
  };

  @action ExtendProductQuote = () => {
    const { validUntil } = this.productQuoteApp;

    if (!this.extendQuoteFormStore.isValid) {
      this.extendQuoteFormStore.alert();
      return;
    }

    const input = new Input({
      input: { id: this.extendProductQuoteId, validUntil },
    });

    const extendQuoteMutation = new ExtendProductQuote(
      input,
      null,
      {
        onSuccess: this.ExtendProductQuoteSuccess,
        onSysErr: this.ExtendProductQuoteErr,
        onUserErr: this.ExtendProductQuoteErr,
      },
      true,
    );

    extendQuoteMutation.process(extendQuote => {
      if (extendQuote === null) {
        return null;
      }

      this.extendQuoteFormStore.SetPosting(false);
      this.SetExtendQuoteDialogVisible(false);
    });

    this.extendQuoteFormStore.SetPosting(true);
    extendQuoteMutation.mutate();
  };

  @action DeleteProductQuote = productQuote => {
    const input = new Input({
      input: { id: productQuote.id },
    });
    const deleteMutation = new DeleteProductQuoteMutation(
      input,
      null,
      {},
      true,
    );
    deleteMutation.process(
      quote => ProductQuoteModel.Load(quote),
      this.CompleteDeleteQuote,
    );
    deleteMutation.mutate();
  };

  @action CompleteDeleteQuote = () => {
    this.SetQuoteDeleted(true);
  };

  @action AddProductQuote = () => {
    if (this.productQuoteApp.submitting) {
      if (this.isValid) {
        this.productQuoteFormStore.SetPosting(true);
        this.createProductQuote.mutate();
      } else {
        this.productQuoteFormStore.SetPosting(false);
        this.productQuoteFormStore.alert();
        this.jobSpecManager.alert();
      }
      this.productQuoteApp.SetSubmitting(false);
    }
  };

  // implement loading state

  @action CompleteAddQuote = quote => {
    this.savedProductQuote = quote;
    this.SetQuoteAdded(true);
    this.productQuoteFormStore.SetPosting(true);
  };
}
